// @ts-strict-ignore
import { Fragment, lazy, memo } from 'react'
import Condition from 'components/Condition'
import ConditionSwitch from 'components/ConditionSwitch'
import ResponsiveDialog from 'components/ResponsiveDialog'
import Suspense from 'components/Suspense'
import { Button, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material'
import { useJobApplication } from 'scenes/Job/useJobApplication'
import { graphql } from 'relay-runtime'
import { PreloadedQuery, usePreloadedQuery } from 'react-relay'
import { JobApplicationDialog_Query } from '__generated__/JobApplicationDialog_Query.graphql'
import { useFragment } from 'react-relay/hooks'
import { useIsClient, useIsMember } from 'hooks/user'
import { JobApplicationDialog_User$key } from '__generated__/JobApplicationDialog_User.graphql'
import { JobApplicationDialog_Job$key } from '__generated__/JobApplicationDialog_Job.graphql'

const JobSpecConfirmationDialog = lazy(() => import(/* webpackChunkName: "JobSpecConfirmationDialog" */'./components/JobSpecConfirmationDialog'))
const FakeApplyDialog = lazy(() => import(/* webpackChunkName: "FakeApplyDialog" */'./components/FakeApplyDialog'))

export const JobApplicationDialogQuery = graphql`
  query JobApplicationDialog_Query($jobSlug: String!, $firmSlug: String!) {
    job(slug: $jobSlug, firmSlug: $firmSlug) {
      ...JobApplicationDialog_Job
    }
    currentUser {
      ...JobApplicationDialog_User
    }
  }
`

interface IJobApplicationDialogProps {
  preloadedQuery: PreloadedQuery<JobApplicationDialog_Query>
  open?: boolean
  onClose: () => void
}

const JobApplicationDialog = (props: IJobApplicationDialogProps) => {
  const { preloadedQuery, open, onClose } = props
  const data = usePreloadedQuery(JobApplicationDialogQuery, preloadedQuery)

  const job = useFragment<JobApplicationDialog_Job$key>(graphql`
    fragment JobApplicationDialog_Job on Job {
      ...useJobApplication_Job
      ...JobSpecConfirmationDialog_Job
    }
  `, data?.job)

  const user = useFragment<JobApplicationDialog_User$key>(graphql`
    fragment JobApplicationDialog_User on User {
      ...useJobApplication_User
      ...JobSpecConfirmationDialog_User

      ...user_useIsMember
      ...user_useIsClient
    }
  `, data?.currentUser)

  const { next, step, redirecting } = useJobApplication(open, job, user)

  const isClient = useIsClient(user)
  const isMember = useIsMember(user)

  return (
    <Fragment>
      <Condition condition={open && !redirecting}>
        <ConditionSwitch>
          <Condition condition={isClient && !isMember}>
            <Suspense noSsr>
              <FakeApplyDialog open close={onClose} />
            </Suspense>
          </Condition>

          <Condition condition={step === 'confirmingContract'}>
            <ResponsiveDialog open onClose={onClose}>
              <DialogTitle>
                Apply Now
              </DialogTitle>

              <DialogContent>
                <DialogContentText>
                  You already have an active contract or job offer.
                  Are you sure you want to apply to this job?
                </DialogContentText>
              </DialogContent>

              <DialogActions>
                <Button onClick={onClose}>
                  Cancel
                </Button>

                <Button onClick={next}>
                  Apply
                </Button>
              </DialogActions>
            </ResponsiveDialog>
          </Condition>

          <Condition condition={step === 'confirmingJobSpec'}>
            <Suspense noSsr>
              <JobSpecConfirmationDialog
                jobFragmentRef={job}
                userFragmentRef={user}
                open
                onClose={onClose}
                onConfirm={next}
              />
            </Suspense>
          </Condition>
        </ConditionSwitch>
      </Condition>
    </Fragment>
  )
}

export default memo(JobApplicationDialog)
